import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Calendar, 
  Building2, 
  DollarSign,
  Clock,
  FileText,
  Download,
  Save,
  AlertTriangle,
  CheckCircle2,
  AlertCircle,
  Target,
  Shield,
  Users,
  Gauge,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { opportunitiesData, recommendations } from "@/data/opportunities";
import { getOpportunityDetails } from "@/data/opportunityDetails";
import { toast } from "sonner";

const RecommendationBadge = ({ recommendation, size = "default" }) => {
  const styles = {
    "Bid": "bg-bid-bg text-bid border-bid/20",
    "No-Bid": "bg-nobid-bg text-nobid border-nobid/20",
    "Partner": "bg-partner-bg text-partner border-partner/20",
  };

  return (
    <Badge 
      variant="outline" 
      className={cn(
        "font-medium border",
        styles[recommendation] || styles["Bid"],
        size === "large" ? "px-4 py-1.5 text-sm" : "px-2.5 py-0.5 text-xs"
      )}
    >
      {recommendation}
    </Badge>
  );
};

const ScorePill = ({ score, size = "default" }) => {
  const getScoreStyle = (score) => {
    if (score >= 80) return "bg-score-high text-success-foreground";
    if (score >= 60) return "bg-score-medium text-success-foreground";
    if (score >= 40) return "bg-score-low text-warning-foreground";
    return "bg-score-poor text-destructive-foreground";
  };

  return (
    <div className={cn(
      "inline-flex items-center justify-center rounded-full font-bold",
      getScoreStyle(score),
      size === "large" ? "w-16 h-10 text-lg" : "w-12 h-7 text-sm"
    )}>
      {score}
    </div>
  );
};

const RiskBadge = ({ risk }) => {
  const config = {
    "Low": { icon: CheckCircle2, className: "text-success bg-success/10 border-success/20" },
    "Medium": { icon: AlertCircle, className: "text-partner bg-partner/10 border-partner/20" },
    "High": { icon: AlertTriangle, className: "text-destructive bg-destructive/10 border-destructive/20" },
  };

  const { icon: Icon, className } = config[risk] || config["Medium"];

  return (
    <div className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border", className)}>
      <Icon className="w-3.5 h-3.5" />
      {risk}
    </div>
  );
};

const ScoreBar = ({ label, value, icon: Icon }) => {
  const getBarColor = (value) => {
    if (value >= 80) return "bg-success";
    if (value >= 60) return "bg-chart-2";
    if (value >= 40) return "bg-warning";
    return "bg-destructive";
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          {Icon && <Icon className="w-4 h-4 text-muted-foreground" />}
          {label}
        </div>
        <span className="text-sm font-semibold text-foreground">{value}%</span>
      </div>
      <div className="h-2.5 bg-muted rounded-full overflow-hidden">
        <div 
          className={cn("h-full rounded-full transition-all duration-500", getBarColor(value))}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
};

export default function OpportunityDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Get base opportunity data
  const baseOpp = useMemo(() => 
    opportunitiesData.find(opp => opp.id === id), 
    [id]
  );
  
  // Get detailed data
  const details = useMemo(() => getOpportunityDetails(id), [id]);
  
  // Local state for editable fields
  const [recommendation, setRecommendation] = useState(baseOpp?.recommendation || "Bid");
  const [notes, setNotes] = useState(details?.notes || "");

  // Handle not found
  if (!baseOpp) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-warning mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Opportunity Not Found</h2>
            <p className="text-muted-foreground mb-6">
              The opportunity with ID "{id}" could not be found.
            </p>
            <Button onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleSave = () => {
    toast.success(`Saved changes for ${id}`, {
      description: `Recommendation: ${recommendation}`
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate("/")}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Pipeline
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold font-heading text-foreground truncate">
                {baseOpp.title}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Section 1: Header Summary */}
        <Card className="shadow-card animate-fade-in">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6">
              {/* Left: Title and Meta */}
              <div className="flex-1 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="font-mono text-sm text-primary font-medium bg-primary/10 px-2 py-1 rounded">
                    {baseOpp.id}
                  </span>
                  <ScorePill score={baseOpp.score} size="large" />
                  <RecommendationBadge recommendation={recommendation} size="large" />
                </div>
                
                <h2 className="text-2xl sm:text-3xl font-bold font-heading text-foreground">
                  {baseOpp.title}
                </h2>
                
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Building2 className="w-4 h-4" />
                    <span className="font-medium text-foreground">{baseOpp.agency}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <FileText className="w-4 h-4" />
                    <span>{baseOpp.vehicle}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Target className="w-4 h-4" />
                    <span>{baseOpp.tech}</span>
                  </div>
                </div>
              </div>

              {/* Right: Key Metrics */}
              <div className="flex flex-wrap lg:flex-col gap-4 lg:gap-3 lg:items-end">
                <div className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-lg">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <div className="text-sm">
                    <span className="text-muted-foreground">Due: </span>
                    <span className="font-semibold text-foreground">
                      {new Date(baseOpp.due).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </span>
                  </div>
                </div>
                
                {details && (
                  <>
                    <div className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-lg">
                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                      <div className="text-sm">
                        <span className="text-muted-foreground">Value: </span>
                        <span className="font-semibold text-foreground">{details.estimatedValue}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-lg">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <div className="text-sm">
                        <span className="text-muted-foreground">PoP: </span>
                        <span className="font-semibold text-foreground">{details.periodOfPerformance}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {details ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Section 2: AI Summary */}
              <Card className="shadow-card animate-slide-up">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <CardTitle className="text-lg font-heading">AI Executive Summary</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">
                    {details.summary}
                  </p>
                </CardContent>
              </Card>

              {/* Section 3: Requirements Tabs */}
              <Card className="shadow-card animate-slide-up" style={{ animationDelay: "50ms" }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-heading">Requirements</CardTitle>
                  <CardDescription>Detailed breakdown by category</CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="technical" className="w-full">
                    <TabsList className="grid grid-cols-4 mb-4">
                      <TabsTrigger value="technical" className="text-xs sm:text-sm">Technical</TabsTrigger>
                      <TabsTrigger value="security" className="text-xs sm:text-sm">Security</TabsTrigger>
                      <TabsTrigger value="staffing" className="text-xs sm:text-sm">Staffing</TabsTrigger>
                      <TabsTrigger value="sla" className="text-xs sm:text-sm">SLA</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="technical" className="mt-0">
                      <ul className="space-y-2">
                        {details.requirements.technical.map((req, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-success mt-0.5 flex-shrink-0" />
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </TabsContent>
                    
                    <TabsContent value="security" className="mt-0">
                      <ul className="space-y-2">
                        {details.requirements.security.map((req, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </TabsContent>
                    
                    <TabsContent value="staffing" className="mt-0">
                      <ul className="space-y-2">
                        {details.requirements.staffing.map((req, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <Users className="w-4 h-4 text-chart-2 mt-0.5 flex-shrink-0" />
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </TabsContent>
                    
                    <TabsContent value="sla" className="mt-0">
                      <ul className="space-y-2">
                        {details.requirements.sla.map((req, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <Gauge className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
                            <span>{req}</span>
                          </li>
                        ))}
                      </ul>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>

              {/* Section 4: Risk Flags */}
              <Card className="shadow-card animate-slide-up" style={{ animationDelay: "100ms" }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-heading">Risk Assessment</CardTitle>
                  <CardDescription>Identified risks and severity levels</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {details.risks.map((risk, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-lg"
                      >
                        <span className="text-sm font-medium">{risk.type}</span>
                        <RiskBadge risk={risk.severity} />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Section 6: Documents */}
              <Card className="shadow-card animate-slide-up" style={{ animationDelay: "150ms" }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-heading">Documents</CardTitle>
                  <CardDescription>RFP and related files</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {details.documents.map((doc, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors group cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium text-foreground">{doc.name}</p>
                            <p className="text-xs text-muted-foreground">{doc.size}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Section 5: Score Breakdown */}
              <Card className="shadow-card animate-slide-up" style={{ animationDelay: "100ms" }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-heading">Score Breakdown</CardTitle>
                  <CardDescription>AI-generated fit analysis</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ScoreBar 
                    label="Technology Alignment" 
                    value={details.scoreBreakdown.technologyAlignment}
                    icon={Target}
                  />
                  <ScoreBar 
                    label="Agency Alignment" 
                    value={details.scoreBreakdown.agencyAlignment}
                    icon={Building2}
                  />
                  <ScoreBar 
                    label="Delivery Feasibility" 
                    value={details.scoreBreakdown.deliveryFeasibility}
                    icon={CheckCircle2}
                  />
                  <ScoreBar 
                    label="Compliance Burden" 
                    value={details.scoreBreakdown.complianceBurden}
                    icon={Shield}
                  />
                  <ScoreBar 
                    label="Competitive Risk" 
                    value={details.scoreBreakdown.competitiveRisk}
                    icon={AlertTriangle}
                  />
                </CardContent>
              </Card>

              {/* Section 7: Capture Actions */}
              <Card className="shadow-card animate-slide-up sticky top-24" style={{ animationDelay: "150ms" }}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-heading">Capture Decision</CardTitle>
                  <CardDescription>Record your bid recommendation</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Recommendation</label>
                    <Select value={recommendation} onValueChange={setRecommendation}>
                      <SelectTrigger className="w-full">
                        <SelectValue>
                          <RecommendationBadge recommendation={recommendation} />
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {recommendations.map(rec => (
                          <SelectItem key={rec} value={rec}>
                            <RecommendationBadge recommendation={rec} />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground">Notes</label>
                    <Textarea
                      placeholder="Add capture notes, key considerations, or action items..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="min-h-[120px] resize-none"
                    />
                  </div>
                  
                  <Button className="w-full" onClick={handleSave}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Decision
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <Card className="shadow-card">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Extended Details Not Available</h3>
              <p className="text-muted-foreground">
                Detailed information for this opportunity has not been loaded yet.
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
